/**
 * Porter Stemmer algorithm for English words.
 * Based on the original algorithm by Martin Porter (1980).
 * TypeScript port from kristopolous/Porter-Stemmer (MIT license).
 *
 * @see https://tartarus.org/martin/PorterAlgorithm/
 */

const step2list: Record<string, string> = {
   ational: 'ate',
   tional: 'tion',
   enci: 'ence',
   anci: 'ance',
   izer: 'ize',
   bli: 'ble',
   alli: 'al',
   entli: 'ent',
   eli: 'e',
   ousli: 'ous',
   ization: 'ize',
   ation: 'ate',
   ator: 'ate',
   alism: 'al',
   iveness: 'ive',
   fulness: 'ful',
   ousness: 'ous',
   aliti: 'al',
   iviti: 'ive',
   biliti: 'ble',
   logi: 'log',
};

const step3list: Record<string, string> = {
   icate: 'ic',
   ative: '',
   alize: 'al',
   iciti: 'ic',
   ical: 'ic',
   ful: '',
   ness: '',
};

const c = '[^aeiou]'; // consonant
const v = '[aeiouy]'; // vowel
const C = `${c}[^aeiouy]*`; // consonant sequence
const V = `${v}[aeiou]*`; // vowel sequence

const mgr0 = new RegExp(`^(${C})?${V}${C}`); // [C]VC... - measure > 0
const meq1 = new RegExp(`^(${C})?${V}${C}(${V})?$`); // [C]VC[V] - measure == 1
const mgr1 = new RegExp(`^(${C})?${V}${C}${V}${C}`); // [C]VCVC... - measure > 1
const s_v = new RegExp(`^(${C})?${v}`); // has vowel in stem

/**
 * Apply the Porter Stemmer algorithm to reduce a word to its stem.
 *
 * @example
 * porterStem('running')  // 'run'
 * porterStem('permits')  // 'permit'
 * porterStem('water')    // 'water'
 */
export function porterStem(w: string): string {
   if (w.length < 3) {
      return w;
   }

   let stem: string;
   let suffix: string;
   let re: RegExp;
   let re2: RegExp;
   let re3: RegExp;
   let re4: RegExp;

   const firstch = w.charAt(0);
   if (firstch === 'y') {
      w = firstch.toUpperCase() + w.slice(1);
   }

   // Step 1a
   re = /^(.+?)(ss|i)es$/;
   re2 = /^(.+?)([^s])s$/;

   if (re.test(w)) {
      w = w.replace(re, '$1$2');
   }
   else if (re2.test(w)) {
      w = w.replace(re2, '$1$2');
   }

   // Step 1b
   re = /^(.+?)eed$/;
   re2 = /^(.+?)(ed|ing)$/;
   if (re.test(w)) {
      const fp = re.exec(w)!;
      re = mgr0;
      if (re.test(fp[1])) {
         re = /.$/;
         w = w.replace(re, '');
      }
   }
   else if (re2.test(w)) {
      const fp = re2.exec(w)!;
      stem = fp[1];
      re2 = s_v;
      if (re2.test(stem)) {
         w = stem;
         re2 = /(at|bl|iz)$/;
         re3 = /([^aeiouylsz])\1$/;
         re4 = new RegExp(`^${C}${v}[^aeiouwxy]$`);
         if (re2.test(w)) {
            w = `${w}e`;
         }
         else if (re3.test(w)) {
            re = /.$/;
            w = w.replace(re, '');
         }
         else if (re4.test(w)) {
            w = `${w}e`;
         }
      }
   }

   // Step 1c
   re = /^(.+?)y$/;
   if (re.test(w)) {
      const fp = re.exec(w)!;
      stem = fp[1];
      re = s_v;
      if (re.test(stem)) {
         w = `${stem}i`;
      }
   }

   // Step 2
   re = /^(.+?)(ational|tional|enci|anci|izer|bli|alli|entli|eli|ousli|ization|ation|ator|alism|iveness|fulness|ousness|aliti|iviti|biliti|logi)$/;
   if (re.test(w)) {
      const fp = re.exec(w)!;
      stem = fp[1];
      suffix = fp[2];
      re = mgr0;
      if (re.test(stem)) {
         w = stem + step2list[suffix];
      }
   }

   // Step 3
   re = /^(.+?)(icate|ative|alize|iciti|ical|ful|ness)$/;
   if (re.test(w)) {
      const fp = re.exec(w)!;
      stem = fp[1];
      suffix = fp[2];
      re = mgr0;
      if (re.test(stem)) {
         w = stem + step3list[suffix];
      }
   }

   // Step 4
   re = /^(.+?)(al|ance|ence|er|ic|able|ible|ant|ement|ment|ent|ou|ism|ate|iti|ous|ive|ize)$/;
   re2 = /^(.+?)(s|t)(ion)$/;
   if (re.test(w)) {
      const fp = re.exec(w)!;
      stem = fp[1];
      re = mgr1;
      if (re.test(stem)) {
         w = stem;
      }
   }
   else if (re2.test(w)) {
      const fp = re2.exec(w)!;
      stem = fp[1] + fp[2];
      re2 = mgr1;
      if (re2.test(stem)) {
         w = stem;
      }
   }

   // Step 5
   re = /^(.+?)e$/;
   if (re.test(w)) {
      const fp = re.exec(w)!;
      stem = fp[1];
      re = mgr1;
      re2 = meq1;
      re3 = new RegExp(`^${C}${v}[^aeiouwxy]$`);
      if (re.test(stem) || (re2.test(stem) && !re3.test(stem))) {
         w = stem;
      }
   }

   re = /ll$/;
   re2 = mgr1;
   if (re.test(w) && re2.test(w)) {
      re = /.$/;
      w = w.replace(re, '');
   }

   // Turn initial Y back to y
   if (firstch === 'y') {
      w = firstch + w.slice(1);
   }

   return w;
}
